import type { FC, ReactNode } from 'react'

import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

type AlertVariant = 'info' | 'success' | 'warning' | 'error'

interface AlertProps {
    variant?: AlertVariant
    title?: string
    children?: ReactNode
    className?: string
    action?: ReactNode
}

const STYLES: Record<AlertVariant, string> = {
    info: 'border-primary/20 bg-primary/5 text-foreground',
    success: 'border-success/25 bg-success-soft text-foreground',
    warning: 'border-warning/25 bg-warning-soft text-foreground',
    error: 'border-destructive/25 bg-danger-soft text-foreground'
}

const ICONS: Record<AlertVariant, ReactNode> = {
    info: <Info className='size-5 text-primary' aria-hidden='true' />,
    success: <CheckCircle2 className='size-5 text-success' aria-hidden='true' />,
    warning: <AlertTriangle className='size-5 text-warning' aria-hidden='true' />,
    error: <XCircle className='size-5 text-destructive' aria-hidden='true' />
}

export const Alert: FC<AlertProps> = ({ variant = 'info', title, children, className, action }): ReactNode => {
    return (
        <div
            role={variant === 'error' ? 'alert' : 'status'}
            className={cn('flex gap-3 rounded-xl border p-4 text-sm', STYLES[variant], className)}
        >
            <div className='mt-px shrink-0'>
                {ICONS[variant]}
            </div>

            <div className='flex min-w-0 flex-1 flex-col gap-1'>
                {title && <p className='font-semibold'>{title}</p>}
                {children && <div className='text-foreground/80 leading-relaxed'>{children}</div>}
                {action && <div className='mt-2'>{action}</div>}
            </div>
        </div>
    )
}
